import styled, { css, keyframes } from 'styled-components';
import { darkMode, darkPalette } from '../../core/theme';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
`;

const riseIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.42);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  animation: ${fadeIn} 0.2s ease;

  ${darkMode(css`
    background: ${darkPalette.overlay};
  `)}
`;

export const Panel = styled.div`
  width: 100%;
  min-height: min(70vh, 100%);
  max-height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px 24px 0;
  background: #fff;
  border-radius: 12px;
  outline: none;
  animation: ${riseIn} 0.25s ease;

  @media screen and (min-width: ${({ theme }) => theme.desktopMinWidth}px) {
    width: 70%;
  }

  ${darkMode(css`
    background: ${darkPalette.surface};
  `)}
`;

export const HeadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

export const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  line-height: 31px;
  color: #171717;

  ${darkMode(css`
    color: ${darkPalette.textPrimary};
  `)}
`;

export const CloseBtn = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;

  &:hover {
    background: #f4f4f4;
  }

  ${darkMode(css`
    &:hover {
      background: ${darkPalette.border};
    }
  `)}
`;

export const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;

  /* the scrollbar sits in the panel's side padding, 8px off the items */
  margin-inline-end: -14px;
  padding-inline-end: 8px;
  padding-bottom: 24px;

  /* GlobalStyles hides every scrollbar; this one is part of the design */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #dedede;
    border-radius: 3px;
  }

  /* iOS draws its scrollbar over the content instead of taking those 6px */
  @supports (-webkit-touch-callout: none) {
    padding-inline-end: 14px;
  }

  /* Firefox */
  @supports not selector(::-webkit-scrollbar) {
    scrollbar-width: thin;
    scrollbar-color: #dedede transparent;
  }

  ${darkMode(css`
    &::-webkit-scrollbar-thumb {
      background: ${darkPalette.border};
    }

    @supports not selector(::-webkit-scrollbar) {
      scrollbar-color: ${darkPalette.border} transparent;
    }
  `)}
`;

export const Section = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;

  @media screen and (min-width: ${({ theme }) => theme.tabletMinWidth}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media screen and (min-width: ${({ theme }) => theme.desktopMinWidth}px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  & + & {
    margin-top: 16px;
  }
`;

export const SectionTitle = styled.h3`
  grid-column: 1 / -1;
  margin-bottom: 6px;
  font-size: 16px;
  font-weight: 700;
  line-height: 20.8px;
  color: #5e5e5e;
  text-align: start;

  ${darkMode(css`
    color: ${darkPalette.textTertiary};
  `)}
`;

export const LangButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 46px;
  padding: 11px 15px; /* 12px 16px together with the border */
  text-align: start;
  border-radius: 12px;
  border: 1px solid ${({ $active }) => ($active ? '#79afe2' : 'transparent')};
  background: ${({ $active }) => ($active ? '#d2e4f5' : 'transparent')};
  cursor: pointer;
  font-size: 16px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  line-height: 22px;
  color: ${({ $active }) => ($active ? '#171717' : '#5e5e5e')};
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ $active }) => ($active ? '#d2e4f5' : '#f4f4f4')};
  }

  &:disabled {
    opacity: 0.65;
    cursor: wait;
  }

  @media screen and (min-width: ${({ theme }) => theme.tabletMinWidth}px) {
    padding: 24px;
    max-height: 70px;
  }

  ${darkMode(css`
    color: ${darkPalette.textTertiary};

    &:hover:not(:disabled) {
      background: ${darkPalette.border};
    }
  `)}
  ${({ $active }) =>
    $active &&
    darkMode(css`
      border-color: ${darkPalette.accent};
      background: ${darkPalette.accentSurface};
      color: ${darkPalette.textPrimary};

      &:hover:not(:disabled) {
        background: ${darkPalette.accentSurface};
      }
    `)}
`;

export const Flag = styled.img`
  width: 28px;
  height: 20px;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  flex-shrink: 0;

  ${darkMode(css`
    border-color: ${darkPalette.border};
  `)}
`;

export const LangLabel = styled.span`
  flex: 1;
  min-width: 0;
`;
